import * as React from 'react';
import './styles.css';
import { useCurrentWorldContext } from '../../context/currentWorld';
import { Feature } from '../../containers/app/productsWrapper';
import { BatteryCentricService, IBatteriesWithCount, ISmartBatteryAlertCount, ISmartBatteryHealthIntervalStats, ISmartBatteryAvgDischargeIntervalStats, ISmartBatteryCycleCountIntervalStats, ISmartBatteryAvgDischargeTimeIntervalStats, ISmartBatteryBatteryAgeIntervalStats, ISmartBatteryFirstSeenIntervalStats } from '../../services/shared/battery/batteryCentricService';
import { useWorldAction } from '../../lib/useWorldAction';
import { BatteryDevicesService, IDevicesAvgDischargeRateIntervalStats, IDevicesAvgDischargeTimeIntervalStats, IDevicesWithCount } from '../../services/shared/device/batteryDevicesService';
import { DataLoading } from '../../components/loading/dataLoading';
import { BatteryOverviewChartsContainer } from './composition/batteryOverviewChartsContainer';

export function BatteryOverviewContainer() {
  const { features } = useCurrentWorldContext();
  const batteryEssentials = features.includes(Feature.batteryEssentials) || features.includes(Feature.panasonicSmartBatteryMonitor);
  const batteryEssentialsDevices = features.includes(Feature.batteryEssentialsDevices) || features.includes(Feature.panasonicSmartBatteryMonitorDevices);

  const batteryService = React.useMemo(() => new BatteryCentricService(), []);
  const deviceService = React.useMemo(() => new BatteryDevicesService(), []);
  const doGetBatteryEstateData = useWorldAction(batteryService.getBatteryEstateData);
  const doGetDeviceEstateData = useWorldAction(deviceService.getDeviceEstateData);
  const doGetSmartBatteryAlertsCount = useWorldAction(batteryService.getSmartBatteryAlertsCount);
  const doGetSmartBatteryHealthIntervalStats = useWorldAction(batteryService.getSmartBatteryHealthIntervalStats);
  const doGetSmartBatteryAverageDischargeStats = useWorldAction(batteryService.getLast30DaysAverageDischargeIntervalStats);
  const doGetSmartBatteryAgeIntervalStats = useWorldAction(batteryService.getLast30DaysBatteryAgeIntervalStats);
  const doGetSmartBatteryFirstSeenIntervalStats = useWorldAction(batteryService.getFirstSeenIntervalStats);
  const doGetSmartBatteryCycleCountStats = useWorldAction(batteryService.getLast30DaysCycleCountIntervalStats);
  const doGetSmartBatteryDischargeTimeStats = useWorldAction(batteryService.getLast30DaysBatteryDischargeTimeIntervalStats);
  const doGetDevicesDischargeTimeStats = useWorldAction(deviceService.getLast30DaysDevicesAvgDischargeTimeIntervalStats);
  const doGetDevicesAverageDischargeRateStats = useWorldAction(deviceService.getLast30DaysDevicesAvgDischargeRateIntervalStats);

  const loadBatteryStateCounts = async (): Promise<{ batteryEstateData: IBatteriesWithCount, deviceEstateData: IDevicesWithCount, batteryAlertsData: ISmartBatteryAlertCount,
    batteryHealthIntervalData: ISmartBatteryHealthIntervalStats, batteryAverageDischargeData: ISmartBatteryAvgDischargeIntervalStats, batteryAgeData: ISmartBatteryBatteryAgeIntervalStats, batteryFirstSeenData: ISmartBatteryFirstSeenIntervalStats,
    batteryCycleCountData: ISmartBatteryCycleCountIntervalStats, batteryAverageDischargeTimeData: ISmartBatteryAvgDischargeTimeIntervalStats,
    deviceAvgDischargeTimeData: IDevicesAvgDischargeTimeIntervalStats, deviceAvgDischargeRateData: IDevicesAvgDischargeRateIntervalStats
   }> => {
    const batteryDataRequest = batteryEssentials ? doGetBatteryEstateData({ underWarranty: false }) : null;
    const deviceDataRequest = batteryEssentialsDevices ? doGetDeviceEstateData() : null;
    const batteryAlertsRequest = batteryEssentials ? doGetSmartBatteryAlertsCount() : null;
    const batteryHealthIntervalStatsRequest = batteryEssentials ? doGetSmartBatteryHealthIntervalStats() : null;
    const batteryAverageDischargeStatsRequest = batteryEssentials ? doGetSmartBatteryAverageDischargeStats() : null;
    const batteryAgeIntervalDataRequest = batteryEssentials ? doGetSmartBatteryAgeIntervalStats() : null;
    const batteryFirstSeenIntervalDataRequest = batteryEssentials ? doGetSmartBatteryFirstSeenIntervalStats() : null;
    const batteryCycleCountStatsRequest = batteryEssentials ? doGetSmartBatteryCycleCountStats() : null;
    const batteryDischargeTimeStatsRequest = batteryEssentials ? doGetSmartBatteryDischargeTimeStats() : null;
    const devicesDischargeTimeStatsRequest = batteryEssentialsDevices ? doGetDevicesDischargeTimeStats() : null;
    const devicesAverageDischargeRateStatsRequest = batteryEssentialsDevices ? doGetDevicesAverageDischargeRateStats() : null;
    const [batteryEstateData, deviceEstateData, batteryAlertsData, batteryHealthIntervalData, batteryAverageDischargeData, batteryAgeData, batteryFirstSeenData, batteryCycleCountData, batteryAverageDischargeTimeData,
      deviceAvgDischargeTimeData, deviceAvgDischargeRateData
    ] = await Promise.all([
      batteryDataRequest,
      deviceDataRequest,
      batteryAlertsRequest,
      batteryHealthIntervalStatsRequest,
      batteryAverageDischargeStatsRequest,
      batteryAgeIntervalDataRequest,
      batteryFirstSeenIntervalDataRequest,
      batteryCycleCountStatsRequest,
      batteryDischargeTimeStatsRequest,
      devicesDischargeTimeStatsRequest,
      devicesAverageDischargeRateStatsRequest
    ]);
    return {
      batteryEstateData,
      deviceEstateData,
      batteryAlertsData,
      batteryHealthIntervalData,
      batteryAverageDischargeData,
      batteryAgeData,
      batteryFirstSeenData,
      batteryCycleCountData,
      batteryAverageDischargeTimeData,
      deviceAvgDischargeTimeData,
      deviceAvgDischargeRateData
    };
  };

  return (
    <div className="battery-overview_container" >
      <DataLoading loadData={loadBatteryStateCounts} transparentOverlay={false} >
        <BatteryOverviewChartsContainer
          data={null}
          isBatteryEssentials={batteryEssentials}
          isBatteryEssentialsDevices={batteryEssentialsDevices}
        />
      </DataLoading>
    </div>
  );
}
